import { useEffect, useState, type KeyboardEvent } from "react";
import { ChevronLeft, ChevronRight, Loader2, Wrench } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import { ToolCallCard } from "./ToolCallCard";
import type { ToolCallEntry } from "./useTurns";

interface ToolCallsDisclosureProps {
  entries: ToolCallEntry[];
  // True while the turn is still running. We auto-expand while running and
  // auto-collapse on completion so the user sees progress live but the
  // finished turn defaults to a compact summary.
  isRunning: boolean;
}

export const ToolCallsDisclosure = ({ entries, isRunning }: ToolCallsDisclosureProps) => {
  const [open, setOpen] = useState(isRunning);
  useEffect(() => {
    setOpen(isRunning);
  }, [isRunning]);

  // Pager state. By default we follow the latest streamed call; once the user
  // navigates we flip into sticky "reading" mode and stop auto-advancing —
  // even if they happen to navigate back onto the latest call. Reading mode
  // resets each time the disclosure is re-opened.
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    if (!reading && entries.length > 0) {
      setSelectedIdx(entries.length - 1);
    }
  }, [entries.length, reading]);

  useEffect(() => {
    if (open) {
      setReading(false);
      setSelectedIdx(Math.max(0, entries.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snap to latest only on open transitions
  }, [open]);

  const move = (delta: number) => {
    const next = selectedIdx + delta;
    if (next < 0 || next >= entries.length) return;
    setSelectedIdx(next);
    setReading(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      move(1);
    } else if (e.key === "Home" && entries.length > 0) {
      e.preventDefault();
      setSelectedIdx(0);
      setReading(true);
    } else if (e.key === "End" && entries.length > 0) {
      e.preventDefault();
      setSelectedIdx(entries.length - 1);
      setReading(true);
    }
  };

  if (entries.length === 0) return null;

  const summary = entries.length === 1 ? "1 tool call" : `${entries.length} tool calls`;
  const current = entries[selectedIdx];
  const atFirst = selectedIdx === 0;
  const atLast = selectedIdx === entries.length - 1;
  const pending = current.result === undefined;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="w-full">
      <CollapsibleTrigger
        className={cn(
          "group inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground",
          "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        )}
      >
        <ChevronRight
          className="size-3.5 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
        <span>{summary}</span>
      </CollapsibleTrigger>
      <CollapsibleContent animated>
        <div
          role="group"
          aria-label="Tool calls"
          tabIndex={0}
          onKeyDown={onKeyDown}
          className={cn(
            "mt-2 overflow-hidden rounded-md border border-border bg-background outline-none",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          )}
        >
          <div className="flex items-center gap-1 border-b border-border px-1 py-1">
            <button
              type="button"
              onClick={() => move(-1)}
              disabled={atFirst}
              aria-label="Previous tool call"
              className={cn(
                "inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground",
                "hover:bg-muted disabled:pointer-events-none disabled:opacity-40",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              <ChevronLeft className="size-3.5" aria-hidden />
            </button>
            <div
              className="flex min-w-0 flex-1 items-center justify-center gap-1.5 text-xs"
              aria-live="polite"
              aria-atomic="true"
            >
              {pending ? (
                <Loader2
                  className="size-3.5 shrink-0 animate-spin text-muted-foreground"
                  aria-hidden
                />
              ) : (
                <Wrench className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              )}
              <span className="truncate font-mono text-[11px] font-medium">{current.name}</span>
              <span className="text-muted-foreground">
                ({selectedIdx + 1}/{entries.length})
              </span>
            </div>
            <button
              type="button"
              onClick={() => move(1)}
              disabled={atLast}
              aria-label="Next tool call"
              className={cn(
                "inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground",
                "hover:bg-muted disabled:pointer-events-none disabled:opacity-40",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              )}
            >
              <ChevronRight className="size-3.5" aria-hidden />
            </button>
          </div>
          <ToolCallCard key={current.id} entry={current} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
